import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Button } from 'reactstrap';

const HEADER_VISIBILITY_WIDTH = 260;
const WINDOW_MARGIN = 12;

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}

function getInitialPosition() {
  return {
    x: Math.max(WINDOW_MARGIN, Math.round(window.innerWidth * 0.08)),
    y: Math.max(WINDOW_MARGIN, Math.round(window.innerHeight * 0.08)),
  };
}

function WarehouseMapFloatingWindow({
  children, dockLabel, onDock, title,
}) {
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [position, setPosition] = useState(getInitialPosition);

  const moveWindow = (event) => {
    if (!dragRef.current || !panelRef.current) return;

    const bounds = panelRef.current.getBoundingClientRect();
    const minimumX = Math.min(WINDOW_MARGIN, window.innerWidth - HEADER_VISIBILITY_WIDTH);
    const maximumX = Math.max(WINDOW_MARGIN, window.innerWidth - bounds.width - WINDOW_MARGIN);
    const maximumY = Math.max(WINDOW_MARGIN, window.innerHeight - 52);

    setPosition({
      x: clamp(
        dragRef.current.x + event.clientX - dragRef.current.pointerX,
        minimumX,
        maximumX,
      ),
      y: clamp(
        dragRef.current.y + event.clientY - dragRef.current.pointerY,
        WINDOW_MARGIN,
        maximumY,
      ),
    });
  };

  const startDragging = (event) => {
    if (event.button !== 0 || event.target.closest('button')) return;
    dragRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: position.x,
      y: position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const stopDragging = (event) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return createPortal(
    <section
      aria-label={title}
      className="warehouse-map-floating"
      ref={panelRef}
      style={{ left: position.x, top: position.y }}
    >
      <header
        className="warehouse-map-floating__header"
        onPointerCancel={stopDragging}
        onPointerDown={startDragging}
        onPointerMove={moveWindow}
        onPointerUp={stopDragging}
      >
        <div className="warehouse-map-floating__title">
          <i className="mdi mdi-drag-horizontal me-2" />
          {title}
        </div>
        <Button color="secondary" outline size="sm" onClick={onDock}>
          <i className="mdi mdi-dock-window me-1" />
          {dockLabel}
        </Button>
      </header>
      <div className="warehouse-map-floating__body">
        {children}
      </div>
      <div aria-hidden="true" className="warehouse-map-floating__resize-hint">
        <i className="mdi mdi-resize-bottom-right" />
      </div>
    </section>,
    document.body,
  );
}

WarehouseMapFloatingWindow.propTypes = {
  children: PropTypes.node.isRequired,
  dockLabel: PropTypes.string.isRequired,
  onDock: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default WarehouseMapFloatingWindow;
